import React from "react";
import { Target, TrendingUp, BarChart3, Zap } from "lucide-react";
import Logo from "../../../assets/image.png";

interface AuthLayoutProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ title, subtitle, children }) => {
  return (
    <div className="min-h-screen flex bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-indigo-700 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-center mb-8">
            <img
              src={Logo}
              alt="Adrsta Logo"
              className="h-16 w-auto bg-white rounded-lg shadow-lg p-2"
            />
          </div>

          <div className="space-y-6 text-white mt-12">
            <h2 className="text-4xl font-bold leading-tight">
              Welcome to Your<br />Marketing Science Platform
            </h2>
          </div>
        </div>

        {/* Features */}
        <div className="relative z-10 grid grid-cols-1 gap-4 text-white">
          <div className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Synthetic Control</h3>
              <p className="text-sm text-blue-100">Measure causal impact with advanced statistical methods</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Marketing Mix Modeling (MMM)</h3>
              <p className="text-sm text-blue-100">Optimize marketing spend across all channels</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Bid Optimization</h3>
              <p className="text-sm text-blue-100">Maximize ROI with intelligent bidding strategies</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Performance Agent</h3>
              <p className="text-sm text-blue-100">One click Measurement and Optimization</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-white/60 text-sm">
          © 2025 Adrsta. All rights reserved.
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img
              src={Logo}
              alt="Adrsta Logo"
              className="h-16 w-auto"
            />
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{title}</h2>
              <p className="text-gray-600">{subtitle}</p>
            </div>

            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
